import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  useMediaQuery,
  TextField,
  Autocomplete
} from '@mui/material';
import { FaChalkboardTeacher } from 'react-icons/fa';
import defaultImage from './classtrackimage.jpg'; // Update if needed

const api = axios.create({ baseURL: 'https://www.janalwilliamsedufoundation.online/' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function formatToLocalTime(timeString) {
  const [hour, minute] = timeString.split(':');
  const date = new Date();
  date.setHours(+hour, +minute, 0, 0);
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
}

function Countdown({ startTime, endTime }) {
  const [status, setStatus] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();

      const start = new Date();
      const [startHr, startMin] = startTime.split(':');
      start.setHours(+startHr, +startMin, 0, 0);

      const end = new Date();
      const [endHr, endMin] = endTime.split(':');
      end.setHours(+endHr, +endMin, 0, 0);

      if (now < start) {
        const diff = start - now;
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setStatus(`Starts in ${mins}m ${secs}s`);
      } else if (now >= start && now <= end) {
        const diff = end - now;
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        setStatus(`Ends in ${mins}m ${secs}s`);
      } else {
        setStatus('Class Ended');
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, endTime]);

  return (
    <h2 style={{ color: 'black', marginTop: '8px' }}>{status}</h2>
  );
}

const SchedulePageTrack = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [gradeClasses, setGradeClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    api.get('api/users/grade-classes/').then(res => setGradeClasses(res.data));
  }, []);

  const handleSelect = async (selected) => {
    if (!selected) return;
    setSelectedClass(selected);
    setLoading(true);
    setShowResult(false);

    const res = await api.get(`class-schedules/?grade_class=${selected.id}`);
    const now = new Date();

    const activeSchedules = res.data.filter(s => {
      const [startHr, startMin] = s.start_time.split(':');
      const [endHr, endMin] = s.end_time.split(':');
      const start = new Date();
      start.setHours(+startHr, +startMin, 0, 0);
      const end = new Date();
      end.setHours(+endHr, +endMin, 0, 0);
      return now >= start && now <= end;
    });

    setTimeout(() => {
      setFilteredSchedules(activeSchedules);
      setLoading(false);
      setShowResult(true);
    }, 3000);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'black',
        color: 'white',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      }}
    >
      {/* Left panel - Class Autocomplete */}
      <Box sx={{ flex: 1, p: 3 }}>
        <Autocomplete
          options={gradeClasses}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => handleSelect(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Grade Class"
              variant="filled"
              sx={{
                backgroundColor: '#222',
                input: { color: 'white' },
                label: { color: 'white' },
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: 48 * 3.5, // Show max 3 with scroll
              overflowY: 'auto',
              color: 'black',
            },
          }}
        />
      </Box>

      {/* Right panel - Class Info */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          backgroundColor: 'gold',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: isMobile ? '300px' : '100vh',
          position: 'relative',
        }}
      >
        {loading ? (
          <CircularProgress size={60} sx={{ color: 'white' }} />
        ) : showResult ? (
          filteredSchedules.length > 0 ? (
            <>
              <Avatar
                src={filteredSchedules[0].profile_image || "/default-avatar.png"}
                alt={filteredSchedules[0].teacher?.full_name || "Teacher"}
                sx={{
                  width: 150,
                  height: 150,
                  border: '4px solid black',
                  mb: 2,
                }}
              />
              <Box textAlign="center">
                <Typography variant="h6" sx={{ color: 'black' }}>
                  {filteredSchedules[0].subject.name}
                </Typography>
                <Typography variant="body1" sx={{ color: 'black' }}>
                  Teacher: {filteredSchedules[0].teacher.full_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'black' }}>
                  Time: {formatToLocalTime(filteredSchedules[0].start_time)} - {formatToLocalTime(filteredSchedules[0].end_time)}
                </Typography>
                <Countdown
                  startTime={filteredSchedules[0].start_time}
                  endTime={filteredSchedules[0].end_time}
                />
              </Box>
            </>
          ) : (
            <Box textAlign="center">
              <FaChalkboardTeacher style={{ fontSize: 80, color: 'black' }} />
              <Typography sx={{ color: 'black', mt: 2 }}>
                No teacher in this class for now!
              </Typography>
            </Box>
          )
        ) : (
          <img src={defaultImage} alt="Default" style={{ width: '80%', maxWidth: 600 }} />
        )}
      </Box>
    </Box>
  );
};

export default SchedulePageTrack;
